import React, { useState, useEffect } from "react";
import "./DynamicWithdrawal.css";
import { IoCloseSharp } from "react-icons/io5";
import { BiChevronLeft } from "react-icons/bi";
import Cookies from "js-cookie";
import { sendNotfication, API } from 'utils/Helper';
import storage from "utils/Storage";
import { useCreateDynamicWithdrawalMutation, useActiveGatewaysQuery, useCurrenciesQuery } from "hooks/usePayment";

function CoinIcon({ gateway, size = 32 }) {
  const colors = ["#f7931a", "#627eea", "#26a17b", "#ef0027", "#345d9d", "#1475e1", "#16a34a"];
  const colorIndex = gateway.currency ? gateway.currency.charCodeAt(0) % colors.length : 0;
  if (gateway.logo_url) {
    return (
      <img
        src={`${API}${gateway.logo_url}`}
        alt={gateway.currency}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "contain" }}
      />
    );
  }
  return (
    <span
      className="dw-coin-fallback"
      style={{ width: size, height: size, background: colors[colorIndex], fontSize: size * 0.42 }}
    >
      {gateway.currency ? gateway.currency.charAt(0) : "W"}
    </span>
  );
}

function TabSwitch({ tab, setTab }) {
  return (
    <div className="dw-tab-switch">
      <button
        className={`dw-tab-btn ${tab === "crypto" ? "active" : ""}`}
        onClick={() => setTab("crypto")}
      >
        Crypto
      </button>
      <button
        className={`dw-tab-btn ${tab === "local" ? "active" : ""}`}
        onClick={() => setTab("local")}
      >
        Local Currency
      </button>
    </div>
  );
}

function CurrencySelectView({ withdrawalGateways, onPickCurrency, currencyTypeMap, currencies }) {
  const [tab, setTab] = useState("crypto");

  // Flatten gateways so that a gateway with multiple currencies (e.g. "USDT,BTC") 
  // is treated as a separate entry for each currency.
  const flatGateways = withdrawalGateways.flatMap(g => {
    if (!g.currency) return [];
    const codes = g.currency.split(',').map(c => c.trim().toUpperCase());
    return codes.map(code => ({
      ...g,
      currency: code
    }));
  });

  const getType = (code) => currencyTypeMap[code] || 'fiat';
  const cryptoGateways = flatGateways.filter(g => getType(g.currency) === "crypto");
  const localGateways  = flatGateways.filter(g => getType(g.currency) !== "crypto");

  const list = tab === "crypto" ? cryptoGateways : localGateways;

  // Group gateways by currency
  const grouped = list.reduce((acc, g) => {
    const code = g.currency;
    if (!acc[code]) acc[code] = [];
    acc[code].push(g);
    return acc;
  }, {});

  const currenciesList = Object.keys(grouped).map(code => {
    const currencyInfo = currencies.find(c => c.code?.toUpperCase() === code) || {};
    return {
      currency: code,
      gateways: grouped[code],
      representative: grouped[code][0],
      iconUrl: currencyInfo.image_url
    };
  });

  return (
    <div className="dw-currency-select-view">
      <TabSwitch tab={tab} setTab={setTab} />
      {currenciesList.length === 0 ? (
        <div className="dw-empty-state">No active methods for {tab === "crypto" ? "Crypto" : "Local Currency"}.</div>
      ) : (
        <div className="dw-currencies-grid">
          {currenciesList.map(c => (
            <button
              key={c.currency}
              className="dw-currency-card-btn"
              onClick={() => onPickCurrency(c)}
            >
              <div className="dw-card-left">
                {c.iconUrl ? (
                  <img src={c.iconUrl.startsWith('http') ? c.iconUrl : `${API}/uploads/currencies/${c.iconUrl}`} alt={c.currency} className="dw-card-img" />
                ) : (
                  <CoinIcon gateway={c.representative} size={30} />
                )}
                <div className="dw-card-details">
                  <span className="dw-card-code">{c.currency}</span>
                  <span className="dw-card-name">
                    {currencies.find(x => x.code?.toUpperCase() === c.currency)?.name || c.currency}
                  </span>
                </div>
              </div>
              <span className="dw-card-arrow">→</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function GatewaySelectView({ currencyObj, onPickGateway }) {
  return (
    <div className="dw-gateway-select-view">
      <div className="dw-subtitle">Select withdrawal method for {currencyObj.currency}:</div>
      <div className="dw-gateways-list">
        {currencyObj.gateways.map(g => (
          <button
            key={g.id}
            className="dw-gateway-row-btn"
            onClick={() => onPickGateway(g)}
          >
            <div className="dw-gw-info">
              <CoinIcon gateway={g} size={32} />
              <div className="dw-gw-text">
                <div className="dw-gw-name">{g.display_name || g.gateway_name}</div>
                <div className="dw-gw-limits">
                  Limit: {g.currency} {g.min_amount} – {g.max_amount || "∞"}
                </div>
              </div>
            </div>
            <span className="dw-gw-action">Select</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function DynamicWithdrawForm({ gateway, onSubmit, isLoading, is2faWithdrawalRequired }) {
  const [amount, setAmount] = useState("");
  const [fieldValues, setFieldValues] = useState({});
  const [twoFaCode, setTwoFaCode] = useState("");

  const getFields = () => {
    try {
      const config = typeof gateway.extra_config === 'string'
        ? JSON.parse(gateway.extra_config || '{}')
        : (gateway.extra_config || {});
      
      if (Array.isArray(config.custom_fields)) {
        return config.custom_fields;
      }
    } catch (e) {}

    // Fallbacks based on gateway type
    if (gateway.gateway_name === "manual_bank") {
      return [
        { label: "Bank Name", key: "bank_name" },
        { label: "Account Holder Name", key: "account_holder" },
        { label: "Account Number", key: "account_number" },
        { label: "IFSC Code", key: "ifsc" }
      ];
    } else {
      return [
        { label: "UPI ID / Wallet Address", key: "address" }
      ];
    }
  };

  const fields = getFields();

  // Check if all fields are filled
  const canSubmit = amount && fields.every(f => fieldValues[f.key] && fieldValues[f.key].trim()) && (!is2faWithdrawalRequired || (twoFaCode && twoFaCode.length === 6));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit || isLoading) return;

    const extra_fields = {
      currency: gateway.currency
    };
    fields.forEach(f => {
      extra_fields[f.key] = fieldValues[f.key].trim();
    });

    // Fallback main address for legacy/general purposes
    const addressField = fields.find(f => f.key === 'address' || f.key === 'account_number' || f.key === 'wallet');
    extra_fields.address = addressField ? fieldValues[addressField.key].trim() : Object.values(fieldValues)[0]?.trim() || '';

    onSubmit({ amount, extra_fields, gateway, twoFaCode });
  };

  return (
    <form onSubmit={handleSubmit} className="dw-form-view">
      <div className="dw-selected-coin-row">
        <CoinIcon gateway={gateway} size={36} />
        <div>
          <div className="dw-sel-code">{gateway.currency}</div>
          <div className="dw-sel-name">{gateway.display_name || gateway.gateway_name}</div>
        </div>
      </div>

      {fields.map(f => (
        <div key={f.key} className="dw-field-group">
          <label>{f.label}</label>
          <div className="dw-input-box">
            <input
              type={f.type || "text"}
              placeholder={`Enter ${f.label.toLowerCase()}`}
              value={fieldValues[f.key] || ""}
              onChange={e => setFieldValues({ ...fieldValues, [f.key]: e.target.value })}
              required
            />
          </div>
        </div>
      ))}

      <div className="dw-field-group">
        <label>Amount ({gateway.currency})</label>
        <div className="dw-input-box">
          <input
            type="number"
            placeholder={`Min: ${gateway.min_amount} – Max: ${gateway.max_amount || "∞"}`}
            value={amount}
            onChange={e => setAmount(e.target.value)}
            required
          />
          <span className="dw-input-suffix">{gateway.currency}</span>
        </div>
        {gateway.min_amount && (
          <p className="dw-limit-hint">Minimum: {gateway.currency} {gateway.min_amount}{gateway.max_amount ? ` | Maximum: {gateway.currency} {gateway.max_amount}` : ""}</p>
        )}
      </div>

      {is2faWithdrawalRequired && (
        <div className="dw-field-group">
          <label>2FA Verification Code</label>
          <div className="dw-input-box">
            <input
              type="text"
              placeholder="Enter 6-digit 2FA code"
              value={twoFaCode}
              onChange={e => setTwoFaCode(e.target.value)}
              maxLength={6}
              required
            />
          </div>
        </div>
      )}

      <button
        type="submit"
        className="dw-action-btn"
        disabled={isLoading || !canSubmit}
      >
        {isLoading ? "Processing…" : `Withdraw ${gateway.currency}`}
      </button>

      {gateway.instructions && (
        <p className="dw-help-text" dangerouslySetInnerHTML={{ __html: gateway.instructions }} />
      )}
    </form>
  );
}

function DynamicWithdrawal({ forceOpen, onForceClose }) {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState("currency-select"); // "currency-select" | "gateway-select" | "withdraw-form"
  const [selectedCurrencyObj, setSelectedCurrencyObj] = useState(null);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [is2faWithdrawalRequired, setIs2faWithdrawalRequired] = useState(false);

  const { data: gateways = [], isLoading } = useActiveGatewaysQuery(isOpen);
  const { data: currencies = [] } = useCurrenciesQuery();
  const withdrawMutation = useCreateDynamicDynamicWithdrawalMutation();

  const currencyTypeMap = currencies.reduce((map, c) => {
    map[c.code?.toUpperCase()] = c.currency_type || 'fiat';
    return map;
  }, {});

  const withdrawalGateways = gateways.filter(g => g.type === "withdrawal");

  useEffect(() => {
    if (forceOpen) {
      setIsOpen(true);
      setStep("currency-select");
      setSelectedCurrencyObj(null);
      setSelectedGateway(null);
      
      const uid = getUidSafe();
      if (uid) {
        fetch(`${API}/2fa/status/${uid}`)
          .then(res => res.json())
          .then(data => {
            if (data.success && data.is2faEnabled && data.twoFaForWithdraw) {
              setIs2faWithdrawalRequired(true);
            } else {
              setIs2faWithdrawalRequired(false);
            }
          })
          .catch(err => console.error("Error fetching 2FA status:", err));
      }
    } else {
      setIsOpen(false);
    }
  }, [forceOpen]);

  const handleClose = () => {
    setIsOpen(false);
    setStep("currency-select");
    setSelectedCurrencyObj(null);
    setSelectedGateway(null);
    if (onForceClose) onForceClose();
  };

  const handlePickCurrency = (currencyObj) => {
    setSelectedCurrencyObj(currencyObj);
    if (currencyObj.gateways.length === 1) {
      setSelectedGateway(currencyObj.gateways[0]);
      setStep("withdraw-form");
    } else {
      setStep("gateway-select");
    }
  };

  const handlePickGateway = (gateway) => {
    setSelectedGateway(gateway);
    setStep("withdraw-form");
  };

  const handleBack = () => {
    if (step === "withdraw-form") {
      if (selectedCurrencyObj?.gateways?.length === 1) {
        setStep("currency-select");
        setSelectedCurrencyObj(null);
        setSelectedGateway(null);
      } else {
        setStep("gateway-select");
        setSelectedGateway(null);
      }
    } else if (step === "gateway-select") {
      setStep("currency-select");
      setSelectedCurrencyObj(null);
    }
  };

  const stepTitle = step === "currency-select"
    ? "Withdrawal"
    : step === "gateway-select"
      ? `Select Gateway (${selectedCurrencyObj?.currency || ""})`
      : `Withdrawal (${selectedGateway?.currency || ""})`;

  const getUidSafe = () => {
    let rawUid = Cookies.get("uid") || Cookies.get("UID");
    if (rawUid) {
      const clean = String(rawUid).replace(/['"]+/g, '').trim();
      const parsed = parseFloat(clean);
      if (!isNaN(parsed)) return parsed;
    }
    const localUid = localStorage.getItem("uid") || localStorage.getItem("user_id");
    if (localUid) {
      const clean = String(localUid).replace(/['"]+/g, '').trim();
      const parsed = parseFloat(clean);
      if (!isNaN(parsed)) return parsed;
    }
    return null;
  };

  const handleWithdrawSubmit = ({ amount, extra_fields, gateway, twoFaCode }) => {
    const userUid = getUidSafe();
    if (!userUid) {
      sendNotfication("User session not found. Please log in again.", "danger", "bottom-left");
      return;
    }

    const payload = {
      uid: userUid,
      money: parseFloat(amount),
      gateway_id: gateway.id,
      extra_fields: extra_fields,
      twoFaCode: twoFaCode
    };

    withdrawMutation.mutate(payload, {
      onSuccess: (data) => {
        if (data.success) {
          sendNotfication(data.message || "Withdrawal request submitted successfully!", "success", "bottom-left");
          handleClose();
        } else {
          sendNotfication(data.message || "Failed to submit withdrawal request", "danger", "bottom-left");
        }
      },
      onError: (error) => sendNotfication(error.message || "An error occurred", "danger", "bottom-left"),
    });
  };

  if (!isOpen) return null;

  return (
    <div className="dw-overlay" onClick={handleClose}>
      <div className="dw-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="dw-header">
          <div className="dw-header-left">
            {step !== "currency-select" && (
              <button className="dw-back-btn" onClick={handleBack}>
                <BiChevronLeft size={18} />
                Back
              </button>
            )}
            {step !== "currency-select" && <span className="dw-slash">/</span>}
            <span className="dw-header-title">{stepTitle}</span>
          </div>
          <button className="dw-close-btn" onClick={handleClose}>
            <IoCloseSharp size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="dw-body">
          {isLoading ? (
            <div className="dw-loader">Loading withdrawal methods…</div>
          ) : step === "currency-select" ? (
            <CurrencySelectView
              withdrawalGateways={withdrawalGateways}
              onPickCurrency={handlePickCurrency}
              currencyTypeMap={currencyTypeMap}
              currencies={currencies}
            />
          ) : step === "gateway-select" ? (
            <GatewaySelectView
              currencyObj={selectedCurrencyObj}
              onPickGateway={handlePickGateway}
            />
          ) : step === "withdraw-form" ? (
            <DynamicWithdrawForm
              gateway={selectedGateway}
              onSubmit={handleWithdrawSubmit}
              isLoading={withdrawMutation.isLoading}
              is2faWithdrawalRequired={is2faWithdrawalRequired}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

// Custom hook matching implementation
function useCreateDynamicDynamicWithdrawalMutation() {
  const [isLoading, setIsLoading] = useState(false);

  const mutate = async (payload, { onSuccess, onError }) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/payment/api/withdrawal/dynamic`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${storage.getKey('token') || Cookies.get('session')}`
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      setIsLoading(false);
      if (response.ok && data.success) {
        if (onSuccess) onSuccess(data);
      } else {
        if (onError) onError(new Error(data.message || "Server error"));
      }
    } catch (err) {
      setIsLoading(false);
      if (onError) onError(err);
    }
  };

  return { mutate, isLoading };
}

export default DynamicWithdrawal;
